import { useEffect, useState } from 'react';
import { useTestContext } from '../../context/testContext';
import { client } from '../../helpers/api-client';
import { useAppointment } from '../../context/appointmentContext';
import { logmarToSnellen } from '../../helpers/data';
import { useResultData } from '../../helpers/useResultData';
import { SessionActions, useSessionUI } from '../../ui';
import { ExamBody, useConfirmNavigation } from './exams/common';
import { AcuityResultsView } from './exams/AcuityResultsView';

// Step 4 results of logMAR / Random E / Kay's (Figma session/11 left, 31 left) and the "View
// Results" screen of Completed Examinations. result_data is built exactly as before (the dashboard
// results renderers read it): { leftEye, rightEye, lettersToUse, corrected, <eye>Snellen, bothEyes? }.
const AcuityResults = ({ localResults, setActiveTab, updateStep }) => {
  const [results, setResults] = useState({});
  const [saving, setSaving] = useState(false);
  const { appointment } = useAppointment();
  const { clearAllResults } = useResultData();
  const { confirm, toast } = useSessionUI();
  const confirmNavigation = useConfirmNavigation();
  const {
    testData: { testResultsId },
  } = useTestContext();

  const getTestResults = (testId) => {
    client(`api/appointment-test-result/${testId}`).then(
      ({ result_data }) => {
        if (result_data) setResults(JSON.parse(result_data));
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const saveTestResults = async () => {
    const { lettersToUse, corrected } = localResults;
    let testResultData = { leftEye: 'Eye not tested', rightEye: 'Eye not tested', lettersToUse, corrected };
    if (localResults.testRightEye && localResults.rightEye) {
      testResultData['rightEye'] = localResults.rightEye.score.toFixed(2);
      testResultData['rightEyeSnellen'] =
        typeof localResults.rightEye.score === 'number' ? logmarToSnellen[localResults.rightEye.score.toFixed(1)] : localResults.rightEye.score;
    }

    if (localResults.testLeftEye && localResults.leftEye) {
      testResultData['leftEye'] = localResults.leftEye.score.toFixed(2);
      testResultData['leftEyeSnellen'] =
        typeof localResults.leftEye.score === 'number' ? logmarToSnellen[localResults.leftEye.score.toFixed(1)] : localResults.leftEye.score;
    }

    if (localResults.testBothEyes && localResults.bothEyes) {
      testResultData['bothEyes'] = localResults.bothEyes.score.toFixed(2);
      testResultData['bothEyesSnellen'] =
        typeof localResults.bothEyes.score === 'number' ? logmarToSnellen[localResults.bothEyes.score.toFixed(1)] : localResults.bothEyes.score;
    }

    const sendTestResultData = {
      appointment_eye_test: localResults.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify(testResultData),
    };

    if (!(await confirm({ message: 'Are you sure you want to save the test results?' }))) return;
    setSaving(true);
    client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
      () => {
        setSaving(false);
        toast({ tone: 'success', title: 'Results saved', body: 'The results were added to this appointment.' });
        setActiveTab('results');
      },
      (error) => {
        setSaving(false);
        console.error('Saving the test results failed', error);
        toast({ tone: 'error', title: 'The results could not be saved', body: error?.message || 'Please try again.' });
      },
    );
  };

  const restartTest = () => {
    confirmNavigation('restart').then(() => {
      clearAllResults();
      return updateStep('eyeSetup');
    });
  };

  useEffect(() => {
    if (!localResults) return getTestResults(testResultsId);
    if (localResults) {
      return setResults({
        bothEyes: localResults.bothEyes?.score?.toFixed(2),
        leftEye: localResults.leftEye?.score?.toFixed(2),
        rightEye: localResults.rightEye?.score?.toFixed(2),
      });
    }
  }, [testResultsId, localResults]);

  return (
    <ExamBody>
      <AcuityResultsView results={results} />
      <SessionActions
        onBack={() => (localResults ? restartTest() : setActiveTab('results'))}
        primary={localResults ? { label: saving ? 'Saving…' : 'Save Test Results', onClick: saveTestResults, disabled: saving } : undefined}
      />
    </ExamBody>
  );
};

export default AcuityResults;
